import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { usePartner } from "../../../contexts/PartnerContext";

// Import existing icons
import logout from "../../../assets/svg-icons/logoutIcon.svg";
import securityGroups from "../../../assets/svg-icons/securityGroupSolid.svg";
import resources from "../../../assets/svg-icons/resources.svg";
import manage from "../../../assets/svg-icons/edit.svg";
import billing from "../../../assets/svg-icons/blackBilling.svg";
import support from "../../../assets/svg-icons/blackSupport.svg";
import setting from "../../../assets/svg-icons/blackSetting.svg";
import arrow from "../../../assets/svg-icons/blueArrow.svg";
import { 
  Database, 
  Globe, 
  Handshake, 
  Activity, 
  ServerCrash, 
  FileLock2, 
  Crown, 
  Shield, 
  Network,
  HardDrive,
  Settings,
  LogOut,
  DollarSign,
  Logs
} from "lucide-react";


const PartnerLink = ({ to, children, className, ...props }) => {
  const { currentSubdomain, isDevMode, isWhiteLabel } = usePartner();
  
  const addPartnerParam = (path) => {
    if (!isDevMode || !isWhiteLabel || !currentSubdomain) {
      return path;
    }
    
    const separator = path.includes('?') ? '&' : '?';
    return `${path}${separator}partner=${currentSubdomain}`;
  };

  const finalTo = addPartnerParam(to);

  return (
    <Link to={finalTo} className={className} {...props}>
      {children}
    </Link>
  );
};

const SidebarDropdown = ({ item, isOpen, toggleDropdown, activeSidebarItem, idleSidebarItem, location }) => {
  const { currentSubdomain, isDevMode, isWhiteLabel } = usePartner();
  
  const addPartnerParam = (path) => {
    if (!isDevMode || !isWhiteLabel || !currentSubdomain) {
      return path;
    }
    
    const separator = path.includes('?') ? '&' : '?';
    return `${path}${separator}partner=${currentSubdomain}`;
  };

  const isActive = item.sublinks?.some((sublink) => {
    const originalPath = sublink.path;
    const partnerAwarePath = addPartnerParam(originalPath);
    return location.pathname === originalPath || location.pathname + location.search === partnerAwarePath;
  });

  return (
    <div className="grid">
      <div
        className={`${isActive ? activeSidebarItem : idleSidebarItem} cursor-pointer group transition-all duration-200`}
        onClick={() => toggleDropdown(item.label)}
      >
        {item.iconComponent ? (
          <div className="h-4 w-4 flex-shrink-0 group-hover:scale-110 transition-transform">{item.iconComponent}</div> 
        ) : (
          <img 
            src={item.icon} 
            alt="" 
            className="h-4 w-4 flex-shrink-0 brightness-0 saturate-100 invert group-hover:scale-110 transition-transform filter" 
          /> 
        )}
        <div className="text-center truncate min-w-0 flex-1 font-medium">{item.label}</div>
        {item.sublinks && (
          <img
            src={arrow}
            alt=""
            className={`h-6 w-6 sm:h-8 sm:w-8 ml-auto transition-all duration-200 flex-shrink-0 brightness-0 saturate-100 invert ${
              isOpen ? "rotate-90" : ""
            }`}
          />
        )}
      </div>
      {item.sublinks && isOpen && (
        <div className="ml-4 sm:ml-6 space-y-[1px] grid animate-slideDown">
          {item.sublinks.map((sublink, index) => {
            const isSubActive = location.pathname === sublink.path || 
                               location.pathname + location.search === addPartnerParam(sublink.path);
            
            return (
              <PartnerLink key={index} to={sublink.path} className="grid">
                <div
                  className={`${
                    isSubActive ? activeSidebarItem : idleSidebarItem
                  } pl-2 sm:pl-4 transition-all duration-200 hover:translate-x-1`}
                >
                  <div className="truncate min-w-0 flex-1 text-xs">{sublink.label}</div>
                </div>
              </PartnerLink>
            );
          })}
        </div>
      )}
    </div>
  );
};

const WhiteLabelSideBar = () => {

  const location = useLocation();
  const { partnerInfo, loading, currentSubdomain, isDevMode, isWhiteLabel } = usePartner();
  
  const addPartnerParam = (path) => {
    if (!isDevMode || !isWhiteLabel || !currentSubdomain) {
      return path;
    }
    
    const separator = path.includes('?') ? '&' : '?';
    return `${path}${separator}partner=${currentSubdomain}`;
  };

  const activeSidebarItem = `flex items-center gap-3 text-white p-2.5 rounded-lg text-xs bg-primaryBlue shadow-md border-l-4 border-white transform scale-105`;
  const idleSidebarItem = "flex items-center gap-3 p-2.5 rounded-lg text-xs text-blue-100 hover:text-white hover:bg-blue-700/50 transition-all duration-200 hover:transform hover:translate-x-1";

  const [dropdownStates, setDropdownStates] = useState({});

  const toggleDropdown = (label) => {
    setDropdownStates((prev) => ({ ...prev, [label]: !prev[label] }));
  };



  const getPartnerSidebarItems = () => {
    return [
      {
        path: "/client-area",
        label: "Resources",
        iconComponent: <ServerCrash className="h-4 w-4 text-white" />,
      },
      {
        path: "/client-area/manage",
        label: "Manage Project",
        iconComponent: <Settings className="h-4 w-4 text-white" />,
      },
      {
          path: "/manageServerSnapshots",
          label: "Server snapshots",
          iconComponent: <ServerCrash className="text-white h-4 w-4"/>,
      },
      {
        label: "Volumes",
        iconComponent: <Database className="h-4 w-4 text-white" />,
        sublinks: [
          { path: "/client-area/volumes", label: "Volumes" },
          { path: "/manageVolumeSnapshots", label: "Volume Snapshots" },
          { path: "/manageVolumeBackups", label: "Volume Backups" },
        ],
      },
      {
        label: "Networks",
        iconComponent: <Network className="h-4 w-4 text-white" />,
        sublinks: [
          { path: "/networks", label: "Networks" },
          { path: "/securityGroups", label: "Security groups" },
          { path: "/routers", label: "Routers" },
          { path: "/floating-ips", label: "Floating IPs" },
          { path: "/manageLoadBalancers", label: "Load Balancers" },
        ],
      },
      {
        path: "/certificate-manager",
        label: "Certificate Manager",
        iconComponent: <FileLock2 className="text-white h-4 w-4"/>,
      },
      {
        path: "/billing",
        label: "Billing",
        iconComponent: <DollarSign className="h-4 w-4 text-white" />,
      },
      {
        path: "/help",
        label: "Support",
        iconComponent: <Handshake className="h-4 w-4 text-white" />,
      },
      {
        path: "/event-logs",
        label: "Event Logs",
        iconComponent: <Logs className="text-white h-4 w-4"/>,
      },
      {
        path: "/settings",
        label: "Settings",
        iconComponent: <Settings className="h-4 w-4 text-white" />,
      },
      {
        path: "/sign-out",
        label: "Logout",
        iconComponent: <LogOut className="h-4 w-4 text-white" />,
      },
    ];
  };

  const checkIsActive = (path) => {
    const partnerAwarePath = addPartnerParam(path);
    return location.pathname === path || location.pathname + location.search === partnerAwarePath;
  };

  return (
    <div className="lg:col-span-2 bg-gradient-to-b from-blue-800 via-blue-900 to-blue-950 h-screen sticky top-0 p-4 rounded-xl m-2 shadow-2xl border border-blue-700 overflow-hidden overflow-y-auto">
      {loading ? (
        <div className="text-white text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
          <p className="mt-2 text-sm">Loading...</p>
        </div>
      ) : (
        <div className="space-y-2">

          {/* Navigation Items */}
          <div className="space-y-1">
            {getPartnerSidebarItems().slice(0, 4).map((item, index) => (
              <div key={index}>
                {item.sublinks ? (
                  <SidebarDropdown
                    item={item}
                    isOpen={dropdownStates[item.label]}
                    toggleDropdown={toggleDropdown}
                    activeSidebarItem={activeSidebarItem}
                    idleSidebarItem={idleSidebarItem}
                    location={location}
                  />
                ) : (
                  <PartnerLink to={item.path}>
                    <div
                      className={`${
                        checkIsActive(item.path) ? activeSidebarItem : idleSidebarItem
                      }`}
                    >
                      {item.iconComponent ? (
                        <div className="h-4 w-4 flex-shrink-0">{item.iconComponent}</div>
                      ) : (
                        <img 
                          src={item.icon} 
                          alt="" 
                          className="h-4 w-4 brightness-0 saturate-100 invert flex-shrink-0" 
                        />
                      )}
                      <div className="text-center truncate font-medium">{item.label}</div>
                    </div>
                  </PartnerLink>
                )}
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className="border-t border-white/20 my-4"></div>

          {/* Secondary Items */}
          <div className="space-y-1">
            {getPartnerSidebarItems().slice(4, -1).map((item, index) => (
              <div key={index}>
                {item.sublinks ? (
                  <SidebarDropdown
                    item={item}
                    isOpen={dropdownStates[item.label]}
                    toggleDropdown={toggleDropdown}
                    activeSidebarItem={activeSidebarItem}
                    idleSidebarItem={idleSidebarItem}
                    location={location}
                  />
                ) : (
                  <PartnerLink to={item.path}>
                    <div
                      className={`${
                        checkIsActive(item.path) ? activeSidebarItem : idleSidebarItem
                      }`}
                    >
                      {item.iconComponent ? (
                        <div className="h-4 w-4 flex-shrink-0">{item.iconComponent}</div>
                      ) : (
                        <img 
                          src={item.icon} 
                          alt="" 
                          className="h-4 w-4 brightness-0 saturate-100 invert flex-shrink-0" 
                        />
                      )}
                      <div className="text-center truncate font-medium">{item.label}</div>
                    </div>
                  </PartnerLink>
                )}
              </div>
            ))}
          </div>

          {/* Bottom Divider */}
          <div className="border-t border-white/20 my-4"></div>

          {/* Logout Section */}
          <PartnerLink to="/sign-out">
            <div className="flex items-center gap-3 p-4 rounded-lg text-xs text-red-200 hover:text-white hover:bg-red-600/50 transition-all duration-200 group">
              <LogOut className="h-4 w-4 flex-shrink-0 group-hover:scale-110 transition-transform" />
              <div className="text-center truncate font-medium">Sign Out</div>
            </div>
          </PartnerLink>

          {/* Footer */}
          <div className="mt-8 p-4 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20">
            <div className="text-center">
              <div className="text-xs text-white/80 mb-2">Powered by</div>
              <div className="text-sm font-bold text-white">CloudVantage</div>
            </div>
          </div>
        </div>
      )}

      {/* Custom styles for animations */}
      <style jsx>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default WhiteLabelSideBar;

